import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useConnectedMerchants,
  useRevokeMerchant,
  useTenantMarketingToggle,
  useM42MarketingToggle,
} from "@orbit/api-client-react";
import type { ConnectedMerchant } from "@orbit/api-zod";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";
import { ConfirmDestructiveAction } from "../components/ConfirmDestructiveAction";

/**
 * /profile/connected-merchants — list of every merchant the customer
 * has joined, with marketing toggles and a revoke action. Plus the
 * platform-wide M42 marketing card pinned to the top.
 *
 * Revoking sets status='REVOKED' but preserves the row + balances —
 * if the user later re-joins via the same Join link, Phase 4's hook
 * (D29) flips it back to ACTIVE so points come back. Revoked rows
 * are de-emphasized but kept visible so the user knows they CAN
 * come back.
 *
 * The revoke mutation lives on this page (not in MerchantRow) so the
 * confirm modal — which is also rendered here — can call it directly
 * with the merchant the user picked.
 */
export function ProfileConnectedMerchantsPage() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const q = useConnectedMerchants({ enabled: isAuthenticated });
  const revokeMut = useRevokeMerchant();

  const [confirmRevoke, setConfirmRevoke] = useState<ConnectedMerchant | null>(
    null,
  );

  if (!isAuthenticated) return <Redirect to="/" />;

  const merchants = q.data?.merchants ?? [];
  const m42 = q.data?.m42MarketingOptIn;

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">
          Connected merchants
        </h1>
        <p className="mt-2 text-sm text-ink-dark">
          Manage which businesses can see your card data and message you.
        </p>

        {m42 && (
          <section className="mt-6 card">
            <h2 className="text-sm font-semibold text-navy">From Orbit (M42)</h2>
            <p className="mt-1 text-xs text-ink-dark">
              Product news, tips, and platform updates.
            </p>
            <div className="mt-3 space-y-2">
              <M42Toggle channel="SMS" label="Text messages" granted={m42.sms} />
              <M42Toggle channel="EMAIL" label="Email" granted={m42.email} />
              <M42Toggle channel="PUSH" label="Push notifications" granted={m42.push} />
            </div>
          </section>
        )}

        <section className="mt-6 space-y-3">
          {q.isLoading && (
            <p className="text-sm text-ink-dark">Loading merchants…</p>
          )}
          {!q.isLoading && merchants.length === 0 && (
            <p className="text-sm text-ink-dark">
              You haven't joined any merchants yet.
            </p>
          )}
          {merchants.map((m) => (
            <MerchantRow
              key={m.tenantCustomerId}
              m={m}
              onRevoke={() => setConfirmRevoke(m)}
            />
          ))}
        </section>
      </div>

      {confirmRevoke && (
        <ConfirmDestructiveAction
          open
          title={`Revoke ${confirmRevoke.tenantName}?`}
          body={
            <p>
              They'll no longer see your card or send you messages. Your
              points and stamps stay safe — re-joining later restores them.
            </p>
          }
          reasonPlaceholder="Tell us why (optional)"
          confirmLabel="Revoke access"
          busy={revokeMut.isPending}
          onCancel={() => setConfirmRevoke(null)}
          onConfirm={async (reason) => {
            await revokeMut.mutateAsync({
              tenantCustomerId: confirmRevoke.tenantCustomerId,
              body: reason ? { reason } : {},
            });
            await qc.invalidateQueries({ queryKey: ["connected-merchants"] });
            await qc.invalidateQueries({ queryKey: ["my-cards"] });
            setConfirmRevoke(null);
          }}
        />
      )}
    </div>
  );
}

function MerchantRow(props: {
  m: ConnectedMerchant;
  onRevoke: () => void;
}) {
  const m = props.m;
  const qc = useQueryClient();
  const toggleMut = useTenantMarketingToggle();
  const isRevoked = m.status === "REVOKED";

  async function toggle(channel: "SMS" | "EMAIL", granted: boolean) {
    await toggleMut.mutateAsync({
      tenantCustomerId: m.tenantCustomerId,
      body: { channel, granted },
    });
    await qc.invalidateQueries({ queryKey: ["connected-merchants"] });
  }

  return (
    <article
      className={`card ${isRevoked ? "opacity-60 border-dashed" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-navy truncate">
            {m.tenantName}
          </h3>
          <p className="text-[11px] uppercase tracking-wider text-ink-dark/70">
            Joined {new Date(m.joinedAt).toLocaleDateString()}
          </p>
          <p className="mt-1 text-xs text-ink-dark">
            {m.totalVisits} visit{m.totalVisits === 1 ? "" : "s"} ·{" "}
            {m.pointsBalance.toFixed(2)} pts · {m.stampsCount} stamps
          </p>
          {isRevoked && (
            <p className="mt-1 text-xs text-red-700">
              Revoked. Re-join via this merchant's link to reconnect.
            </p>
          )}
        </div>
      </div>

      {!isRevoked && (
        <div className="mt-3 space-y-2">
          <ToggleRow
            label="Allow text messages from them"
            granted={m.marketingOptIn.sms}
            onChange={(g) => toggle("SMS", g)}
            busy={toggleMut.isPending}
          />
          <ToggleRow
            label="Allow emails from them"
            granted={m.marketingOptIn.email}
            onChange={(g) => toggle("EMAIL", g)}
            busy={toggleMut.isPending}
          />
        </div>
      )}

      {!isRevoked && (
        <button
          type="button"
          onClick={props.onRevoke}
          className="mt-3 w-full text-sm font-medium text-red-700 underline"
        >
          Revoke access
        </button>
      )}
    </article>
  );
}

function ToggleRow(props: {
  label: string;
  granted: boolean;
  onChange: (granted: boolean) => Promise<void> | void;
  busy?: boolean;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-navy">
      <span>{props.label}</span>
      <input
        type="checkbox"
        checked={props.granted}
        disabled={props.busy}
        onChange={(e) => void props.onChange(e.target.checked)}
        className="h-5 w-5 rounded border-navy/30 text-navy focus:ring-navy"
      />
    </label>
  );
}

function M42Toggle(props: {
  channel: "SMS" | "EMAIL" | "PUSH";
  label: string;
  granted: boolean;
}) {
  const qc = useQueryClient();
  const mut = useM42MarketingToggle();
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-navy">
      <span>{props.label}</span>
      <input
        type="checkbox"
        checked={props.granted}
        disabled={mut.isPending}
        onChange={async (e) => {
          await mut.mutateAsync({
            channel: props.channel,
            granted: e.target.checked,
          });
          await qc.invalidateQueries({ queryKey: ["connected-merchants"] });
        }}
        className="h-5 w-5 rounded border-navy/30 text-navy focus:ring-navy"
      />
    </label>
  );
}
